import { useCallback, useEffect, useRef, useState } from "react";
import ForceGraph2D, { type ForceGraphMethods } from "react-force-graph-2d";
import type { DagSlice } from "./api";
import { DEFAULT_DAG_VIEW, fitGraph, graphData, nodeRadius, pointerRadius, type VizData, type VizNode, type VizLink } from "./dag-layout";

interface Props {
  slice: DagSlice;
  onSelect: (txId: string) => void;
  selected: string | null;
  width: number;
  height: number;
}

const STATUS_COLOR: Record<string, string> = {
  TENTATIVE: "#f5a623",
  FINAL: "#21c598",
  INVALID: "#ff5f6d",
};

const KIND_RING: Record<number, string> = {
  0: "#3aa6ff", // transfer
  1: "#b67dff", // witness
  6: "#7c87a4", // join
};

export default function DagViz({
  slice,
  onSelect,
  selected,
  width,
  height,
}: Props) {
  const ref = useRef<ForceGraphMethods<VizNode, VizLink> | undefined>(
    undefined,
  );
  const [hover, setHover] = useState<string | null>(null);

  const [graph, setGraph] = useState<{ slice: DagSlice; data: VizData }>(() => ({ slice, data: graphData(slice) }));
  if (graph.slice !== slice) setGraph({ slice, data: graphData(slice, graph.data) });
  const data = graph.data;
  const camera = useRef({ moved: false, settled: false, pending: true });
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const selection = useRef(selected);
  useEffect(() => {
    if (selection.current !== selected) camera.current.moved = true;
    selection.current = selected;
  }, [selected]);
  const fit = useCallback(() => {
    const g = ref.current;
    if (!g || camera.current.moved || !camera.current.settled || !camera.current.pending) return;
    const target = fitGraph(g.getGraphBbox(), width, height);
    if (!target) return;
    camera.current.pending = false;
    const center = g.centerAt();
    if (center.x !== target.x || center.y !== target.y) g.centerAt(target.x, target.y);
    if (g.zoom() !== target.zoom) g.zoom(target.zoom);
  }, [width, height]);
  useEffect(() => { camera.current.settled = false; }, [data]);
  useEffect(() => { camera.current.pending = true; fit(); }, [fit]);

  return (
    <div className="relative" onWheelCapture={() => { camera.current.moved = true; }}
      onPointerDownCapture={e => { pointer.current = { x: e.clientX, y: e.clientY }; }}
      onPointerMoveCapture={e => {
        const start = pointer.current;
        if (start && (start.x !== e.clientX || start.y !== e.clientY)) camera.current.moved = true;
      }}
      onPointerUpCapture={() => { pointer.current = null; }}
      onPointerCancelCapture={() => { pointer.current = null; }}>
    <button type="button" className="absolute z-10 right-2 top-2 bg-mesh-panel px-2 py-1 rounded text-xs"
      onClick={() => { camera.current.moved = false; camera.current.pending = true; fit(); }}>Reset view</button>
    <ForceGraph2D
      ref={ref}
      graphData={data}
      width={width}
      height={height}
      backgroundColor="#0b1020"
      nodeId="id"
      linkSource="source"
      linkTarget="target"
      linkColor={() => "rgba(124,135,164,0.4)"}
      linkDirectionalArrowLength={4}
      linkDirectionalArrowRelPos={1}
      onNodeHover={(n) => setHover((n as VizNode | null)?.id ?? null)}
      onNodeClick={(n) => { camera.current.moved = true; onSelect((n as VizNode).id); }}
      onNodeDrag={() => { camera.current.moved = true; }}
      onEngineStop={() => { camera.current.settled = true; fit(); }}
      nodeRelSize={DEFAULT_DAG_VIEW.selectedRadius + DEFAULT_DAG_VIEW.ringWidth / 2}
      nodePointerAreaPaint={(node, color, ctx, scale) => {
        const n = node as VizNode & { x: number; y: number };
        ctx.beginPath();
        ctx.arc(n.x, n.y, pointerRadius(selected === n.id, hover === n.id, scale), 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
      }}
      nodeCanvasObject={(node, ctx, globalScale) => {
        const n = node as VizNode & { x: number; y: number };
        const isSelected = selected === n.id;
        const isHovered = hover === n.id;
        const r = nodeRadius(isSelected, isHovered);
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
        ctx.fillStyle = STATUS_COLOR[n.status] ?? "#7c87a4";
        ctx.fill();
        const ring = KIND_RING[n.kind];
        if (ring) {
          ctx.strokeStyle = ring;
          ctx.lineWidth = DEFAULT_DAG_VIEW.ringWidth;
          ctx.stroke();
        }
        if (isSelected || isHovered) {
          ctx.fillStyle = "#e7ecf6";
          ctx.font = `${DEFAULT_DAG_VIEW.labelSize / globalScale}px JetBrains Mono`;
          ctx.fillText(n.id.slice(0, 8) + "…", n.x + DEFAULT_DAG_VIEW.labelOffset, n.y + 3);
        }
      }}
      cooldownTicks={DEFAULT_DAG_VIEW.cooldownTicks}
    />
    </div>
  );
}
